/**
 * UI-30: Action Orchestration Engine — Rule Mappings
 *
 * Deterministic rules that map alert types to root problems,
 * root problems to domains, and root problems to executable actions.
 *
 * NO randomness. NO time-based drift. Pure data mappings.
 *
 * PILOT-23: Action titles and context lines use supervisor-level language.
 * No system jargon (signals, clusters, "operational recovery", etc.).
 */

import type {
  RootProblemType,
  ActionDomain,
  ActionPriority,
  ActionCTA,
  SignalCluster,
  PressureState,
  Action,
  ActionTargetItem,
} from './actionTypes';

// ─── Alert Type → Root Problem ───────────────────────────────────────────────

/**
 * Maps each known alert type string to its root problem.
 * Multiple alert types can collapse into one root problem (deduplication).
 */
export const ALERT_TO_ROOT_PROBLEM: Record<string, RootProblemType> = {
  // Housekeeping cluster — only fires from HOUSEKEEPING_SURGE signal (actual room backlog)
  HOUSEKEEPING_OVERLOAD: 'HOUSEKEEPING_BACKLOG',
  // BACKLOG_CRITICAL fires from general request queue backlog (backlogSeverityScore > 15)
  // — not housekeeping-specific. Maps to SERVICE_DEGRADING → navigate to requests queue.
  BACKLOG_CRITICAL: 'SERVICE_DEGRADING',

  // Capacity cluster
  CAPACITY_STRAIN: 'CAPACITY_OVERLOAD',
  WORKLOAD_CRITICAL: 'WORKLOAD_CRITICAL',

  // SLA cluster
  SLA_BREACH_RISK: 'SLA_BREACH_RISK',

  // Maintenance cluster
  MAINTENANCE_ESCALATION: 'MAINTENANCE_ESCALATION',

  // Surge cluster
  REQUEST_SURGE: 'REQUEST_SURGE',

  // Service cluster
  SERVICE_DEGRADING: 'SERVICE_DEGRADING',
  OPERATIONS_CRITICAL: 'SERVICE_DEGRADING',

  // Hotel-level cluster
  HOTEL_AT_RISK: 'HOTEL_CRITICAL',
  HOTEL_CRITICAL: 'HOTEL_CRITICAL',
};

// ─── Risk Type → Root Problem ────────────────────────────────────────────────

export const RISK_TO_ROOT_PROBLEM: Record<string, RootProblemType> = {
  RESPONSE_DELAY_RISK: 'SLA_BREACH_RISK',
  SERVICE_CAPACITY_RISK: 'CAPACITY_OVERLOAD',
  REQUEST_SURGE_RISK: 'REQUEST_SURGE',
  // UNRESOLVED_REQUESTS = general operations risk (requests not completed)
  // — not housekeeping-specific. Maps to SERVICE_DEGRADING → navigate to requests queue.
  UNRESOLVED_REQUESTS: 'SERVICE_DEGRADING',
};

// ─── Root Problem → Domain ───────────────────────────────────────────────────

export const ROOT_PROBLEM_DOMAIN: Record<RootProblemType, ActionDomain> = {
  HOUSEKEEPING_BACKLOG: 'housekeeping',
  CAPACITY_OVERLOAD: 'staff',
  SLA_BREACH_RISK: 'operations',
  MAINTENANCE_ESCALATION: 'maintenance',
  REQUEST_SURGE: 'operations',
  WORKLOAD_CRITICAL: 'staff',
  SERVICE_DEGRADING: 'operations',
  HOTEL_CRITICAL: 'operations',
};

// ─── Root Problem → Action Template ──────────────────────────────────────────

interface ActionTemplate {
  title: string;
  description: string;
  contextTemplate: string;
  cta: ActionCTA;
}

// PILOT-23: All titles use supervisor-level language — a concrete verb + a
// concrete subject. No system jargon ("operational recovery", "service quality
// degradation", "capacity strain"). A first-day floor supervisor reads the
// title and immediately knows what physical action to take.
export const ROOT_PROBLEM_ACTION: Record<RootProblemType, ActionTemplate> = {
  HOUSEKEEPING_BACKLOG: {
    title: 'Send housekeeping to rooms waiting for a clean',
    description:
      'Assign available housekeeping staff to overdue and high-priority rooms to clear the backlog.',
    contextTemplate: 'Rooms waiting for cleaning',
    cta: { type: 'dispatch', payload: { target: 'housekeeping', action: 'clear-backlog' } },
  },
  CAPACITY_OVERLOAD: {
    title: 'Redistribute tasks — staff are overloaded',
    description:
      'Move pending tasks from overloaded staff to available team members.',
    contextTemplate: 'Staff at capacity',
    cta: { type: 'assign', payload: { target: 'staff', action: 'rebalance-workload' } },
  },
  SLA_BREACH_RISK: {
    title: 'Move overdue guest requests to in-progress',
    description:
      'Assign requests approaching SLA breach to available staff immediately.',
    contextTemplate: 'Requests past response time',
    // OPS-38.2: 'prioritize' verb — this CTA assigns NEW requests, it does
    // NOT escalate. Previously routed via ctaType='escalate' which silently
    // dispatched under escalation language.
    cta: { type: 'prioritize', payload: { target: 'requests', action: 'prioritize-overdue' } },
  },
  MAINTENANCE_ESCALATION: {
    title: 'Escalate maintenance issues that have stalled',
    description:
      'Route stalled maintenance tickets to senior technicians or external contractors.',
    contextTemplate: 'Maintenance jobs stuck without progress',
    cta: { type: 'escalate', payload: { target: 'maintenance', action: 'escalate-stalled' } },
  },
  REQUEST_SURGE: {
    title: 'Extra hands needed — request volume is spiking',
    description:
      'Deploy additional front-desk and concierge staff to handle the request spike.',
    contextTemplate: 'More requests coming in than usual',
    cta: { type: 'dispatch', payload: { target: 'operations', action: 'surge-response' } },
  },
  WORKLOAD_CRITICAL: {
    title: 'Call in more staff — current team is at the limit',
    description:
      'Staff workload is critical. Call in off-duty staff or redistribute tasks immediately.',
    contextTemplate: 'Not enough staff to cover current workload',
    cta: { type: 'assign', payload: { target: 'staff', action: 'emergency-staffing' } },
  },
  SERVICE_DEGRADING: {
    title: 'Check on unanswered guest requests',
    description:
      'Several requests have not been picked up. Review the open queue and assign them.',
    contextTemplate: 'Guest requests not yet picked up',
    cta: { type: 'navigate', to: '/requests' },
  },
  HOTEL_CRITICAL: {
    title: 'Handle all overdue requests and tasks now',
    description:
      'Multiple departments are behind. Assign overdue items across housekeeping, operations, and maintenance.',
    contextTemplate: 'Multiple departments behind',
    // OPS-38.2: 'recover' verb — hotel-wide recovery dispatches work; it is
    // not an escalation. Escalated items remain ESCALATED through recovery
    // and wait for explicit supervisor dispatch (preserving the escalation
    // narrative as required by OPS-38).
    cta: { type: 'recover', payload: { target: 'hotel', action: 'operational-recovery' } },
  },
};

// ─── Severity Weights ────────────────────────────────────────────────────────

const SEVERITY_WEIGHT: Record<string, number> = {
  critical: 40,
  warning: 20,
  info: 5,
};

// ─── Root Problem Base Urgency ───────────────────────────────────────────────

const ROOT_PROBLEM_URGENCY: Record<RootProblemType, number> = {
  HOTEL_CRITICAL: 30,
  SLA_BREACH_RISK: 25,
  HOUSEKEEPING_BACKLOG: 20,
  CAPACITY_OVERLOAD: 20,
  WORKLOAD_CRITICAL: 20,
  MAINTENANCE_ESCALATION: 15,
  REQUEST_SURGE: 15,
  SERVICE_DEGRADING: 10,
};

// ─── Priority Derivation ─────────────────────────────────────────────────────

export function derivePriority(cluster: SignalCluster): ActionPriority {
  if (cluster.maxSeverity === 'critical') return 'critical';
  if (cluster.maxSeverity === 'warning') return 'high';
  return 'medium';
}

// ─── Impact Score Computation ────────────────────────────────────────────────

/**
 * Deterministic impact score:
 *   severityWeight + guestImpactWeight + timeCriticalityWeight + systemPressureWeight
 *
 * Range: 0–100 (clamped)
 */
export function computeImpactScore(
  cluster: SignalCluster,
  pressure: PressureState,
): number {
  // 1. Severity weight — highest severity in cluster
  const severityWeight = SEVERITY_WEIGHT[cluster.maxSeverity] ?? 5;

  // 2. Guest impact — risk count drives guest-facing urgency
  const guestImpactWeight = Math.min(cluster.risks.length * 10, 25);

  // 3. Time criticality — root problem urgency
  const timeCriticalityWeight = ROOT_PROBLEM_URGENCY[cluster.rootProblem] ?? 10;

  // 4. System pressure — inverse of pulse score (lower pulse = more pressure)
  const systemPressureWeight = Math.max(0, Math.round((100 - pressure.pulseScore) * 0.15));

  const raw = severityWeight + guestImpactWeight + timeCriticalityWeight + systemPressureWeight;
  return Math.min(100, Math.max(0, raw));
}

// ─── Target Items Derivation (PILOT-23) ──────────────────────────────────────

/**
 * Filters live requests to the items relevant to this cluster's domain,
 * sorted oldest first. Returns up to MAX_TARGET_ITEMS.
 *
 * PILOT-28: For the operations domain (SLA/surge/service degrading), prefers
 * SLA-breached items so action cards show rooms actually past the threshold.
 * Falls back to all pending items only when none are breached.
 * For housekeeping/maintenance, filters by domain as before.
 * Returns undefined for staff domain (no room pills on staffing cards).
 */
const MAX_TARGET_ITEMS = 5;

export function deriveTargetItems(
  cluster: SignalCluster,
  liveRequests: readonly ActionTargetItem[] | undefined,
): readonly ActionTargetItem[] | undefined {
  if (!liveRequests || liveRequests.length === 0) return undefined;

  // Staff domain actions are about staffing levels, not specific rooms.
  // Showing room pills on a "call in more staff" card is misleading.
  if (cluster.domain === 'staff') return undefined;

  // PILOT-28: Operations domain (SLA/surge/service degrading cards) is
  // cross-department — don't restrict by domain. Instead, prefer SLA-breached
  // items so the card shows rooms actually past the threshold, not merely-pending
  // ones. Falls back to all pending items only when nothing is breached.
  if (cluster.domain === 'operations') {
    const breached = liveRequests.filter(r => r.slaBreached === true);
    const items = breached.length > 0 ? breached : liveRequests;
    return items.slice(0, MAX_TARGET_ITEMS);
  }

  // Housekeeping / maintenance: prefer items matching the cluster's domain
  // (housekeeping card shows housekeeping rooms, maintenance shows issues).
  const domainMatch = liveRequests.filter(r => r.domain === cluster.domain);
  if (domainMatch.length > 0) return domainMatch.slice(0, MAX_TARGET_ITEMS);

  return undefined;
}

// ─── Context Line Derivation ─────────────────────────────────────────────────

/**
 * Derives a human-readable context line for an action.
 * PILOT-23: When targetItems are available, names specific rooms and request
 * types. Falls back to signal-count text when data is unavailable.
 *
 * No system jargon. A first-day floor supervisor reads this and immediately
 * understands what is happening.
 */
export function deriveContextLine(
  cluster: SignalCluster,
  templateLine: string,
  targetItems?: readonly ActionTargetItem[],
): string {
  // PILOT-23: when specific room pills are available, the context line uses
  // the template text so the pills carry the specifics exclusively.
  // This prevents the double-render where both text and pills show the same rooms.
  if (targetItems && targetItems.length > 0) {
    return templateLine;
  }

  // Fallback: operational counts (no specific data available)
  const alertCount = cluster.alerts.length;
  const riskCount = cluster.risks.length;
  if (riskCount > 0 && alertCount > 0) {
    return `${templateLine} — affecting guest experience`;
  }
  if (alertCount > 1) {
    return `${alertCount} related issues — ${templateLine.toLowerCase()}`;
  }
  return templateLine;
}

// ─── Confidence Derivation ───────────────────────────────────────────────────

/**
 * Derives a confidence label from impact score and cluster strength.
 * Deterministic: same inputs → same label.
 */
export function deriveConfidence(
  impactScore: number,
  cluster: SignalCluster,
): Action['confidence'] {
  const signalCount = cluster.alerts.length + cluster.risks.length;
  if (impactScore >= 60 && signalCount >= 2) return 'high';
  if (impactScore >= 40 || cluster.maxSeverity === 'critical') return 'recommended';
  return 'suggested';
}
